import { PiBookOpen, PiBriefcase, PiCompass, PiQuestion, PiSquaresFour, PiTag, PiUser } from 'react-icons/pi';
import { matchSitePages, matchCategories, matchHelpArticles, defaultQuickLinks } from '../../constants/siteSearchIndex';

// One query, seven groups, one set of destination links — shared by the
// header dropdown (SiteSearch) and the full results page (/search) so the
// two can never drift into disagreeing about what a search finds or where a
// result goes. Three of the groups are matched client-side from static
// indexes (site pages, taxonomy categories, Help Center articles); the other
// four come from /api/search, which the caller fetches and passes in as
// `dynamic`.

export const GROUP_ICON = {
  pages: PiCompass,
  categories: PiSquaresFour,
  help: PiQuestion,
  freelancers: PiUser,
  listings: PiTag,
  jobPostings: PiBriefcase,
  courses: PiBookOpen,
};

export const GROUP_LABEL = {
  pages: 'Pages',
  categories: 'Categories',
  help: 'Help Center',
  freelancers: 'Freelancers',
  listings: 'Services',
  jobPostings: 'Jobs',
  courses: 'Courses',
};

export const EMPTY_DYNAMIC = { freelancers: [], listings: [], jobPostings: [], courses: [] };

// Some accounts still carry HTML in freelancerProfile.headline from before it
// was a plain-text field — shown raw it would read as literal markup.
export function stripHtml(str) {
  return String(str || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

// `limit` caps each client-side group (the DB groups are already capped by
// the API's own limit). `quickLinksWhenEmpty` is the dropdown's behavior of
// offering a few destinations before anything is typed; the results page has
// no empty state to fill, so it leaves it off.
export function buildSearchGroups({ query, user, dynamic, limit = 5, quickLinksWhenEmpty = false }) {
  const trimmed = (query || '').trim();
  const d = dynamic || EMPTY_DYNAMIC;
  const groups = [];

  const pages = trimmed
    ? matchSitePages(trimmed, user, limit + 1)
    : (quickLinksWhenEmpty ? defaultQuickLinks(user) : []);
  if (pages.length) {
    groups.push({ key: 'pages', items: pages.map((p) => ({ id: p.path, label: p.label, path: p.path })) });
  }

  const categoryHits = trimmed ? matchCategories(trimmed, limit) : [];
  if (categoryHits.length) {
    // Lands exactly like clicking the category/subcategory in the Browse
    // Services filter rail would — see HireTalent.jsx's ?category=/
    // ?subcategory= seeding effect.
    groups.push({
      key: 'categories',
      items: categoryHits.map((c) => ({
        id: `${c.category}::${c.subcategory || ''}`,
        label: c.label,
        path: c.subcategory
          ? `/hire-talent?category=${encodeURIComponent(c.category)}&subcategory=${encodeURIComponent(c.subcategory)}`
          : `/hire-talent?category=${encodeURIComponent(c.category)}`,
      })),
    });
  }

  const helpHits = trimmed ? matchHelpArticles(trimmed, limit) : [];
  if (helpHits.length) {
    groups.push({
      key: 'help',
      items: helpHits.map((h) => ({ id: h.id, label: h.label, sub: h.sub, path: h.path })),
    });
  }

  if (d.freelancers?.length) {
    groups.push({
      key: 'freelancers',
      items: d.freelancers.map((f) => ({
        id: f._id,
        label: `${f.firstName} ${f.lastName}`,
        sub: stripHtml(f.freelancerProfile?.headline),
        avatar: f.profilePhoto,
        path: `/freelancer/${f._id}`,
      })),
    });
  }

  if (d.listings?.length) {
    groups.push({
      key: 'listings',
      items: d.listings.map((l) => ({
        id: l._id,
        label: l.title,
        sub: l.category,
        avatar: l.photos?.[0], // no category-art stand-in — falls to the group icon if there's no real photo
        path: `/hire-talent/listing/${l._id}`,
      })),
    });
  }

  if (d.jobPostings?.length) {
    groups.push({
      key: 'jobPostings',
      items: d.jobPostings.map((j) => ({
        id: j._id,
        label: j.title,
        sub: j.category,
        avatar: j.photos?.[0],
        path: `/job-postings/${j._id}`,
      })),
    });
  }

  if (d.courses?.length) {
    groups.push({
      key: 'courses',
      items: d.courses.map((c) => ({
        id: c._id,
        label: c.title,
        sub: c.category,
        avatar: c.thumbnail || null,
        path: `/academy/courses/${c._id}`,
      })),
    });
  }

  return groups;
}
